import { Injectable } from '@angular/core';

import { AnalyticsEvent } from '../../Domain/Model/analytics.event';
import { ApiErrorEvent } from '../../Domain/Model/Events/System/api-error.event';
import { AppLaunchEvent } from '../../Domain/Model/Events/System/app-launch.event';
import { ErrorOccurredEvent } from '../../Domain/Model/Events/System/error-occurred.event';
import { PerformanceEvent } from '../../Domain/Model/Events/System/performance.event';
import { ButtonClickEvent, ButtonClickEventContext } from '../../Domain/Model/Events/UserJourney/button-click.event';
import { NavigationEvent } from '../../Domain/Model/Events/UserJourney/navigation.event';
import { UserLoginEvent } from '../../Domain/Model/Events/UserJourney/user-login.event';
import { AnalyticsService } from '../../Domain/Port/analytics.service';

/**
 * Mock implementation of AnalyticsService.
 * Stores events in memory and provides methods to retrieve them.
 */
@Injectable({
    providedIn: 'root'
})
export class InMemoryAnalyticsService extends AnalyticsService {
    private events: AnalyticsEvent[] = [];

    override async logCustomEvent(event: AnalyticsEvent): Promise<void> {
        this.registerEvent(event);
    }

    /**
     * Reset all stored events. Useful between tests.
     */
    reset(): void {
        this.events = [];
    }

    /**
     * Get all events logged during the test
     */
    getAllEvents(): AnalyticsEvent[] {
        return [...this.events];
    }

    /**
     * Get all button click events
     */
    getButtonClickEvents(): AnalyticsEvent[] {
        return this.events.filter(event => event.name === 'button_click');
    }

    /**
     * Get all navigation events
     */
    getNavigationEvents(): AnalyticsEvent[] {
        return this.events.filter(event => event.name === 'page_view');
    }

    /**
     * Get all app launch events
     */
    getAppLaunchEvents(): AnalyticsEvent[] {
        return this.events.filter(event => event.name === 'app_launch');
    }

    /**
     * Get all user login events
     */
    getUserLoginEvents(): AnalyticsEvent[] {
        return this.events.filter(event => event.name === 'user_login');
    }

    /**
     * Get all error events
     */
    getErrorEvents(): AnalyticsEvent[] {
        return this.events.filter(event => event.name === 'error_occurred');
    }

    /**
     * Get all performance events
     */
    getPerformanceEvents(): AnalyticsEvent[] {
        return this.events.filter(event => event.name === 'performance_metric');
    }

    /**
     * Get all API error events
     */
    getApiErrorEvents(): AnalyticsEvent[] {
        return this.events.filter(event => event.name === 'api_error');
    }

    override async logButtonClick(buttonId: string, form: string, context?: ButtonClickEventContext): Promise<void> {
        const event = new ButtonClickEvent(buttonId, form, context);
        this.registerEvent(event);
    }

    override async logNavigation(from: string, to: string): Promise<void> {
        const event = new NavigationEvent(from, to);
        this.registerEvent(event);
    }

    override async logAppLaunch(): Promise<void> {
        const event = new AppLaunchEvent();
        this.registerEvent(event);
    }

    override async logUserLogin(method: string): Promise<void> {
        const event = new UserLoginEvent(method);
        this.registerEvent(event);
    }

    override async logError(errorType: string, errorMessage: string): Promise<void> {
        const event = new ErrorOccurredEvent(errorType, errorMessage);
        this.registerEvent(event);
    }

    override async logPerformance(action: string, startTime: number, success: boolean = true): Promise<void> {
        const duration = Date.now() - startTime;
        const event = new PerformanceEvent(action, duration, success);
        this.registerEvent(event);
    }

    override async logApiError(
        endpoint: string,
        statusCode: number,
        request: Record<string, unknown>,
        response: Record<string, unknown>
    ): Promise<void> {
        const event = new ApiErrorEvent(endpoint, statusCode, request, response);
        this.registerEvent(event);
    }

    private registerEvent(event: AnalyticsEvent): void {
        console.info(`Fired event: ${event.name} with params:`, event.params);
        this.events.push(event);
    }
}
